import { useState, useContext, useEffect, useMemo, FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { VisibilityOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Button, useMediaQuery } from '@mui/material';
import { getBuildOptionReports } from '../../fetch';
import { IBuildOptionReport } from '../../models';
import { UserContext } from '../../context';
import { Page, PageTitle, Pagination, Table } from '../../components';
import { useHistory } from 'react-router-dom';
import { BuildSelectedOptionsTableResponsiveComponent } from './BuildSelectedOptionsTableResponsiveComponent';
import { BuildSelectedOptionsFilters } from './BuildSelectedOptionsFilters';
import { BuildSelectedOptionsPDFModal } from './build-selected-options-pdf-modal';
import { SalesContext, update } from '../../context/sales';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment } from '../../helpers';

interface IBuildSelectedOptions {
  isTab?: boolean;
  path?: string;
}
const PAGE_NAME = 'reports-build-selected-options';
export const BuildSelectedOptions: FC<IBuildSelectedOptions> = ({ isTab = false, path }) => {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  // Test data
  const [buildReports, setBuildReports] = useState<IBuildOptionReport[] | []>([]);
  const [isLoadingReports, setLoadingReports] = useState<boolean>(true);
  // state
  const [neighborhoodId, setNeighborhoodId] = useState<string | number>('');
  const [lot, setLot] = useState<string | number>('');

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPageContext = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const isFromSales = path?.includes('sales');
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [openPdfModal, setOpenPdfModal] = useState<boolean>(false);
  const [selectedBuildReport, setSelectedBuildReport] = useState<IBuildOptionReport | null>(null);

  // context
  const { isWarrantyAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
  } | null>({});

  const fetchBuildOptionReports = async () => {
    try {
      setLoadingReports(true);
      const res = await getBuildOptionReports({
        page: page + 1,
        perPage: isFromSales ? perPageContext : perPage,
        sortBy: neighborhoodId ? 'LotNumber' : 'NeighborhoodId',
        sortDirection: 'Asc',
        isActive: isWarrantyAdmin ? null : true,
        ...filters
      });

      setBuildReports(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading build selected options, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchBuildOptionReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchBuildOptionReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPageContext, perPage, filters]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        sort: false,
        overrideWidth: 600
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false,
        overrideWidth: 600
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        overrideWidth: 600,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildOptionReport } };
        }) => {
          return (
            <div>
              <Button
                color="primary"
                startIcon={<VisibilityOutlined />}
                className={classes.actionButton}
                onClick={() => {
                  handleViewDetails(original);
                }}
              >
                View Selected Options
              </Button>
              {(!!original?.buildSelectedOptionsPdfUrl ||
                !!original?.buildSelectedOptionsSlimPdfUrl) && (
                <>
                  <Button
                    id={`pdf-modal-${original.neighborhoodId}-${original.lotNumber}`}
                    color="secondary"
                    startIcon={<InsertDriveFileOutlined />}
                    onClick={() => {
                      setSelectedBuildReport(original);
                      setOpenPdfModal(true);
                    }}
                  >
                    PDF
                  </Button>
                </>
              )}
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewDetails = (page: IBuildOptionReport) => {
    history.push(
      path
        ? `${path}/${page.neighborhoodId}/${page.lotNumber}`
        : `/documentation/build-selected-options/${page.neighborhoodId}/${page.lotNumber}`
    );
  };

  return (
    <>
      <Page
        pageHasStickyHeaders
        title="Build Selected Options"
        isModal={isTab}
        showSideBar={isTab ? false : undefined}
        showSideNav={isTab ? false : undefined}
        showBreadcrumbs={isTab ? false : undefined}
        hasNoContentPadding={isTab ? true : undefined}
        printOrientation="landscape"
      >
        {!isTab && <PageTitle title="Build Selected Options" />}
        <BuildSelectedOptionsFilters
          isLoading={isLoadingReports}
          selectedNeighborhoodId={neighborhoodId}
          selectedLotId={lot}
          setSelectedNeighborhoodId={setNeighborhoodId}
          setSelectedLotId={setLot}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                neighborhoodId: '',
                lotNumber: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                neighborhoodId: neighborhoodId || undefined,
                lotNumber: lot || undefined
              });
            }
          }}
        />
        <Table
          stickyHeaders
          mobileBreakPoint={Infinity}
          columns={columns}
          data={buildReports}
          isLoading={isLoadingReports}
          hidePagination
          noResultsText="No Build Selected Options Found"
          ResponsiveComponent={isMobile ? BuildSelectedOptionsTableResponsiveComponent : undefined}
          mobileProps={{
            setOpenPdfModal,
            setSelectedBuildReport
          }}
        />
        {!isLoadingReports && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={isFromSales ? perPageContext : perPage}
            setPage={setPage}
            rowsPerPageOptions={isFromSales ? SALES_PORTAL_PER_PAGE_OPTIONS : undefined}
            setRowsPerPage={itemsPerPage => {
              if (isFromSales) {
                return dispatchPerPage(
                  update({
                    pageName: PAGE_NAME,
                    environment: getEnvironment(),
                    itemsPerPage: itemsPerPage
                  })
                );
              } else {
                setRowsPerPage(itemsPerPage);
              }
            }}
          />
        )}
        <BuildSelectedOptionsPDFModal
          open={openPdfModal}
          onClose={() => {
            setOpenPdfModal(false);
          }}
          currentReport={selectedBuildReport}
        />
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButton: {
    textAlign: 'right',
    fontSize: 13,
    '&& + &&': {
      marginLeft: theme.spacing(0.5)
    },
    '&& .MuiButton-startIcon': {
      position: 'relative',
      top: '-2px'
    }
  }
}));
